import { isPending, type MetricValue, type SupportedLocale } from '@fleetcore/shared'

import { formatNumber } from '../../i18n/format'
import type { DashboardTile, PeriodCoverage, SegmentAnalytics, TileDirection } from './api-types'

/**
 * Presentation rules for §8.8. Pure, so every one of them is testable without a DOM.
 *
 * ─── THE PERCENTAGE TRAP ─────────────────────────────────────────────────────
 *
 * The API sends percentage POINTS: `utilizationPct: 42.5` means 42.5 %. `Intl`'s
 * `style: 'percent'` multiplies by 100, so handing it 42.5 renders **4 250 %**. That is
 * not a hypothetical — it is what happens the first time somebody reuses the app's
 * generic `format.percent()` here, and 4 250 % utilisation is the kind of number that
 * makes a customer stop reading the page. Points are divided by 100 exactly once, in
 * `formatPercentPoints`, and `coverage.fraction` — which really is a 0..1 ratio — goes
 * through `formatFraction` instead. Both are tested against each other.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Numbers
 * ──────────────────────────────────────────────────────────────────────────── */

export function formatPercentPoints(locale: SupportedLocale, points: number, digits = 1): string {
  return formatNumber(locale, points / 100, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** A 0..1 coverage, not a rate. `formatPercentPoints(0.35)` would render "0,4 %". */
export function formatFraction(locale: SupportedLocale, fraction: number, digits = 0): string {
  return formatNumber(locale, fraction, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/**
 * A signed change, with U+2212 MINUS rather than a hyphen.
 *
 * The hyphen-minus is narrower than the plus sign, so a column of deltas rendered with
 * it does not line up, and at small sizes "-4 %" reads as "4 %" with a stray dash.
 */
export function formatSignedPercentPoints(
  locale: SupportedLocale,
  points: number,
  digits = 1,
): string {
  const body = formatPercentPoints(locale, Math.abs(points), digits)
  if (points > 0) return `+${body}`
  if (points < 0) return `−${body}`
  return body
}

const currencyFormatters = new Map<string, Intl.NumberFormat>()

export function formatMoney(
  locale: SupportedLocale,
  amount: number,
  currency: string,
  digits = 0,
): string {
  const key = `${locale}|${currency}|${digits}`
  const cached = currencyFormatters.get(key)
  if (cached !== undefined) return cached.format(amount)

  try {
    const formatter = new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
    currencyFormatters.set(key, formatter)
    return formatter.format(amount)
  } catch {
    // An unrecognised ISO code is a tenant configuration mistake, not a reason to
    // blank the tile: the number is still right and the code is still shown.
    return `${formatNumber(locale, amount, { maximumFractionDigits: digits })} ${currency}`
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pending reasons
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The API's reason keys are namespaced `analytics.*` and `dashboard.*` — see
 * `analytics.rollup.ts` and `dashboards.tiles.ts`, which emit them. i18next resolves a
 * key with no `:` against the DEFAULT namespace (`common`), so an untouched
 * `dashboard.empty_fleet` would be looked up as `common:dashboard.empty_fleet` and
 * silently miss. Prefixing puts it in this module's own catalogue, where the FR/EN
 * pair actually lives.
 *
 * A key this build does not know is left ALONE rather than mapped to a guess:
 * `MetricTile` then falls back to the wording for the availability class, which is
 * vaguer but still true. Inventing a sentence for an unknown reason is how a UI ends
 * up telling a customer the wrong thing about why a figure is missing.
 */
export function localisedReasonKey(reasonKey: string): string {
  return `dashboard:reasons.${reasonKey}`
}

export function withLocalisedReason(metric: MetricValue<number>): MetricValue<number> {
  if (!isPending(metric) || metric.reasonKey === undefined) return metric
  return { ...metric, reasonKey: localisedReasonKey(metric.reasonKey) }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tiles
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How one tile's value reads, given the unit the API attached to it.
 *
 * The unit is NOT inferred from the tile key. `fuel_cost_month` carries an ISO 4217
 * code that depends on what the tenant's receipts were denominated in, and a client
 * that assumed MAD would relabel a French customer's euros without changing the digits
 * — an error of about a factor of eleven, rendered confidently.
 */
export function formatTileValue(
  tile: Pick<DashboardTile, 'kind' | 'unit'>,
  locale: SupportedLocale,
  value: number,
): string {
  switch (tile.kind) {
    case 'currency':
      return tile.unit === null
        ? formatNumber(locale, value, { maximumFractionDigits: 0 })
        : formatMoney(locale, value, tile.unit)
    case 'percentage':
      return formatPercentPoints(locale, value)
    case 'quantity':
      return formatQuantity(locale, value, tile.unit)
    case 'count':
    case 'list':
      return formatNumber(locale, value, { maximumFractionDigits: 0 })
  }
}

function formatQuantity(locale: SupportedLocale, value: number, unit: string | null): string {
  // Kilometres to the unit, litres and hours to a decimal: a fleet's daily distance is
  // read in thousands and the last metre is noise, whereas "0,4 L" and "0 L" are
  // different claims about a machine that idled for twenty minutes.
  const digits = unit === 'km' ? 0 : 1
  const body = formatNumber(locale, value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  return unit === null ? body : `${body} ${unit}`
}

/**
 * Which way to colour a delta. The DIRECTION comes from the API because only it knows
 * the question: +18 % of fuel is bad, +18 % of utilisation is good, and a client that
 * coloured every rise green would congratulate a fleet for burning more diesel.
 */
export type DeltaTone = 'success' | 'danger' | 'neutral'

export function deltaTone(direction: TileDirection, deltaPoints: number): DeltaTone {
  if (direction === 'neutral' || deltaPoints === 0) return 'neutral'
  const good = direction === 'up_is_good' ? deltaPoints > 0 : deltaPoints < 0
  return good ? 'success' : 'danger'
}

/* ────────────────────────────────────────────────────────────────────────────
 * The partial period (rule 9)
 * ──────────────────────────────────────────────────────────────────────────── */

export interface CoverageBreakdown {
  /** `day` once the period is longer than two days, `hour` below that. */
  readonly unit: 'day' | 'hour'
  readonly elapsed: number
  readonly total: number
}

const DAY_S = 86_400
const HOUR_S = 3_600

/**
 * "12 jours sur 31" — the sentence that stops a partial month reading as a collapse.
 *
 * Elapsed is rounded UP and floored at 1: on the 12th at 09:00, 11 days and 9 hours
 * have elapsed, and the honest label is "12 jours sur 31" because the twelfth day is
 * the one in progress. Rounding down would claim eleven days of data for a screen that
 * is showing twelve days of it.
 *
 * Below two days the same sentence in days would read "1 jour sur 1", which says
 * nothing at all on the period where partiality matters most — a day view opened at
 * 09:00 is a third of a day — so short windows are counted in hours. Total is rounded
 * to the nearest unit rather than truncated: a 25-hour Moroccan DST day is one day, and
 * a 23-hour one is not "0 days".
 */
export function coverageBreakdown(coverage: PeriodCoverage): CoverageBreakdown {
  const unit: 'day' | 'hour' = coverage.totalS > 2 * DAY_S ? 'day' : 'hour'
  const size = unit === 'day' ? DAY_S : HOUR_S
  const total = Math.max(1, Math.round(coverage.totalS / size))
  const elapsed = Math.min(total, Math.max(1, Math.ceil(coverage.elapsedS / size)))
  return { unit, elapsed, total }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Segments (rule 3)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The unit a segment is metered in. Machines are hours and road vehicles are
 * kilometres — `usageMetricFor()` forked the roster server-side and this is the same
 * fork on the label, so a figure can never appear under the wrong unit.
 */
export function segmentUsageUnit(segment: SegmentAnalytics): 'km' | 'h' {
  return segment.unit === 'kilometre' ? 'km' : 'h'
}

export function consumptionUnitLabel(segment: SegmentAnalytics): 'l_per_100km' | 'l_per_hour' {
  return segment.consumptionUnit
}

/**
 * Litres wasted at idle, as a share of the litres the segment burned.
 *
 * Returns null rather than 0 when either figure is unmeasured — a "0 %" here would
 * read as a fleet with no idling problem, which is the opposite of "we could not
 * measure the idling".
 */
export function idleShareOfFuel(segment: SegmentAnalytics): number | null {
  const wasted = segment.idling.wastedL.value
  const total = segment.fuelL.value
  if (wasted === null || total === null || total <= 0) return null
  return (wasted / total) * 100
}
